import useClarityEvent from './useClarityEvent';
import { useLocation } from 'react-router-dom';
import { sendEventService } from '../shared/services/events.service';
import useAmplitude from './useAmplitude';
import { useCallback } from 'react';
import usePlatform from './usePlatform';
import { getEventSource } from '../shared/utils/general.util';
import { getSessionRecordingIds } from '../shared/analytics/sessionRecording';
import { logger } from '../shared/utils/logger';

const useEvents = () => {
	const location = useLocation();
	const { trackEvent: trackEventClarity } = useClarityEvent();
	const { trackEvent: trackEventAmplitude } = useAmplitude();
	const { isMobile } = usePlatform();

	const trackEvent = useCallback(async ({
		eventName,
		eventPayload = {},
		sendToClarity = true,
		sendToAmplitude = true,
	}: {
		eventName: string;
		eventPayload?: Record<string, any>;
		sendToClarity?: boolean;
		sendToAmplitude?: boolean;
	}) => {
		const promises = [];

		// Amarre de la grabación: el par device/session de Amplitude viaja con cada
		// evento a NUESTRA base. Es lo que permite abrir el replay de un usuario
		// desde nexus sin llamar a Amplitude ni mapear user_id → amplitude_id: el
		// replay_id ES `${device}/${session}`. Amplitude ya lo sabe de su lado; sin
		// esta copia, nuestra base no.
		const { ampDeviceId, ampSessionId } = getSessionRecordingIds();

		promises.push(
			sendEventService({
				event: eventName,
				source: getEventSource(isMobile),
				eventPayload: {
					...eventPayload,
					amp_device_id: ampDeviceId,
					amp_session_id: ampSessionId,
				},
			}).catch((error) => {
				logger.error('Error tracking event to Clevertap:', error);
			})
		);

		if (sendToClarity) {
			promises.push(
				Promise.resolve()
					.then(() => trackEventClarity(eventName))
					.catch((error) => {
						logger.error('Error tracking event to Clarity:', error);
					})
			);
		}

		if (sendToAmplitude) {
			promises.push(
				trackEventAmplitude(eventName, eventPayload).catch((error) => {
					logger.error('Error tracking event to Amplitude:', error);
				})
			);
		}

		await Promise.allSettled(promises);
	}, [isMobile]);

	const sendEvent = useCallback(async (
		eventName: string,
		eventPayload: Record<string, any>,
	) => {
		try {
			await sendEventService({
				event: eventName,
				source: getEventSource(isMobile),
				eventPayload: {
					...eventPayload,
					section: location.pathname,
				},
			});
		} catch (_) {}
	}, [isMobile, location.pathname]);

	return { trackEvent, sendEvent, trackEventClarity };
};

export default useEvents;
